"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SaddleBaseProvider = void 0;
const bignumber_1 = require("@ethersproject/bignumber");
const amm_1 = require("@zenlink-interface/amm");
const chain_1 = require("@zenlink-interface/chain");
const currency_1 = require("@zenlink-interface/currency");
const router_config_1 = require("@zenlink-interface/router-config");
const jsbi_1 = __importDefault(require("jsbi"));
const abis_1 = require("../abis");
const entities_1 = require("../entities");
const LiquidityProvider_1 = require("./LiquidityProvider");
class SaddleBaseProvider extends LiquidityProvider_1.LiquidityProvider {
    constructor(chainId, client) {
        super(chainId, client);
        this.poolCodes = [];
        this.fetchedTokens = new Set();
    }
    async getPools(tokens) {
        if (this.basePools[this.chainId] === undefined) {
            this.lastUpdateBlock = -1;
            return [];
        }
        const [, , poolsStable] = await this.getStablePools(tokens);
        if (poolsStable.length) {
            this.poolCodes = [...this.poolCodes, ...poolsStable];
            ++this.stateId;
        }
    }
    async getStablePools(tokens, needPoolCode = true) {
        // create token map: token address => token
        const tokenMap = new Map();
        tokens.forEach((t) => {
            if (!this.fetchedTokens.has(t))
                this.fetchedTokens.add(t);
            tokenMap.set(t.address.toLowerCase(), t);
        });
        const allPools = [...(this.basePools[this.chainId] || []), ...(this.metaPools[this.chainId] || [])];
        const [poolAddresses, tokensAddresses, lpAddresses, basePoolAddresses] = allPools.reduce((memo, pool) => {
            const [poolAddress, tokenAddresses, lpAddress, basePoolAddress] = pool;
            memo[0].push(poolAddress);
            memo[1].push(tokenAddresses);
            memo[2].push(lpAddress);
            memo[3].push(basePoolAddress);
            return memo;
        }, [[], [], [], []]);
        const tokenBalancesPromise = Promise.all(poolAddresses.map((address, i) => this.client.multicall({
            multicallAddress: this.client.chain?.contracts?.multicall3?.address,
            allowFailure: true,
            contracts: tokensAddresses[i].map((_, index) => ({
                address: address,
                chainId: chain_1.chainsParachainIdToChainId[this.chainId],
                abi: abis_1.saddleBase,
                functionName: 'getTokenBalance',
                args: [index],
            })),
        })));
        const swapStoragePromise = this.client.multicall({
            multicallAddress: this.client.chain?.contracts?.multicall3?.address,
            allowFailure: true,
            contracts: poolAddresses.map(addr => ({
                address: addr,
                chainId: chain_1.chainsParachainIdToChainId[this.chainId],
                abi: abis_1.saddleBase,
                functionName: 'swapStorage',
            })),
        });
        const getAPromise = this.client.multicall({
            multicallAddress: this.client.chain?.contracts?.multicall3?.address,
            allowFailure: true,
            contracts: poolAddresses.map(addr => ({
                address: addr,
                chainId: chain_1.chainsParachainIdToChainId[this.chainId],
                abi: abis_1.saddleBase,
                functionName: 'getA',
            })),
        });
        const getVirtualPricePromise = this.client.multicall({
            multicallAddress: this.client.chain?.contracts?.multicall3?.address,
            allowFailure: true,
            contracts: poolAddresses.map(addr => ({
                address: addr,
                chainId: chain_1.chainsParachainIdToChainId[this.chainId],
                abi: abis_1.saddleBase,
                functionName: 'getVirtualPrice',
            })),
        });
        const totalSupplysPromise = this.client.multicall({
            multicallAddress: this.client.chain?.contracts?.multicall3?.address,
            allowFailure: true,
            contracts: lpAddresses.map(addr => ({
                address: addr,
                chainId: chain_1.chainsParachainIdToChainId[this.chainId],
                abi: abis_1.saddleBase,
                functionName: 'totalSupply',
            })),
        });
        const [tokenBalances, swapStorage, A, virtualPrices, totalSupplys,] = await Promise.all([
            tokenBalancesPromise,
            swapStoragePromise,
            getAPromise,
            getVirtualPricePromise,
            totalSupplysPromise,
        ]);
        const poolCodes = [];
        const stableSwaps = [];
        const metaSwaps = [];
        const baseSwapMap = new Map();
        poolAddresses.forEach((addr, i) => {
            const lpToken = lpAddresses[i];
            const tokens = tokensAddresses[i];
            const balances = tokenBalances[i].every(b => b.result !== undefined)
                ? tokenBalances[i].map(b => bignumber_1.BigNumber.from(b.result).toString())
                : undefined;
            const storage = swapStorage[i]?.result;
            const a = A[i].result ? bignumber_1.BigNumber.from(A[i].result) : undefined;
            const virtualPrice = virtualPrices[i].result ? bignumber_1.BigNumber.from(virtualPrices[i].result) : undefined;
            const totalSupply = totalSupplys[i].result ? bignumber_1.BigNumber.from(totalSupplys[i].result) : undefined;
            if (tokens
                && lpToken
                && balances
                && storage
                && a
                && virtualPrice
                && totalSupply
                && tokens.every(addr => tokenMap.get(addr.toLowerCase()) !== undefined)) {
                const liquidityToken = new currency_1.Token({
                    chainId: this.chainId,
                    address: lpToken,
                    decimals: 18,
                    symbol: 'SBL',
                    name: 'Saddle Based LiquidityToken',
                });
                const pooledTokens = tokens.map(address => tokenMap.get(address.toLowerCase()));
                const swap = new amm_1.StableSwap(this.chainId, addr, pooledTokens, liquidityToken, currency_1.Amount.fromRawAmount(liquidityToken, totalSupply.toString()), balances.map((balance, i) => currency_1.Amount.fromRawAmount(pooledTokens[i], balance)), jsbi_1.default.BigInt(bignumber_1.BigNumber.from(storage[4]).toString()), jsbi_1.default.BigInt(bignumber_1.BigNumber.from(storage[5]).toString()), jsbi_1.default.BigInt(a.toString()), jsbi_1.default.BigInt(virtualPrice.toString()));
                const fee = Number(storage[4]) / 1e10;
                const baseSwapAddress = basePoolAddresses[i];
                if (baseSwapAddress) {
                    const baseSwap = baseSwapMap.get(baseSwapAddress.toLowerCase());
                    if (baseSwap) {
                        metaSwaps.push(swap);
                        if (needPoolCode) {
                            const basePooledTokens = baseSwap.pooledTokens;
                            const tokensLengthExcludeLP = swap.pooledTokens.length - 1;
                            if (tokensLengthExcludeLP > 1) {
                                for (let i = 0; i < tokensLengthExcludeLP; i++) {
                                    for (let j = i + 1; j < tokensLengthExcludeLP; j++) {
                                        const basePool = new entities_1.StablePool(swap, pooledTokens[i], pooledTokens[j], fee);
                                        poolCodes.push(new entities_1.StablePoolCode(basePool, this.getPoolProviderName()));
                                        ++this.stateId;
                                    }
                                }
                            }
                            for (let i = 0; i < tokensLengthExcludeLP; i++) {
                                for (let j = 0; j < basePooledTokens.length; j++) {
                                    const metaPool = new entities_1.MetaPool(baseSwap, swap, pooledTokens[i], basePooledTokens[j], fee);
                                    poolCodes.push(new entities_1.MetaPoolCode(metaPool, this.getPoolProviderName()));
                                    ++this.stateId;
                                }
                            }
                        }
                    }
                }
                else {
                    baseSwapMap.set(addr.toLowerCase(), swap);
                    stableSwaps.push(swap);
                    if (needPoolCode) {
                        for (let i = 0; i < pooledTokens.length; i++) {
                            for (let j = i + 1; j < pooledTokens.length; j++) {
                                const pool = new entities_1.StablePool(swap, pooledTokens[i], pooledTokens[j], fee);
                                poolCodes.push(new entities_1.StablePoolCode(pool, this.getPoolProviderName()));
                                ++this.stateId;
                            }
                        }
                    }
                }
            }
        });
        return [stableSwaps, metaSwaps, poolCodes];
    }
    async updatePoolsData() {
        if (!this.poolCodes.length)
            return;
        const pools = this.poolCodes.map(pc => pc.pool);
        const [stableSwaps, metaSwaps] = await this.getStablePools(Array.from(this.fetchedTokens), false);
        const swapMap = new Map();
        const swaps = [...stableSwaps, ...metaSwaps];
        swaps.forEach((swap) => {
            swapMap.set(swap.contractAddress, swap);
        });
        pools.forEach((pool) => {
            if (pool instanceof entities_1.StablePool) {
                const swap = swapMap.get(pool.swap.contractAddress);
                if (swap) {
                    pool.updateSwap(swap);
                    ++this.stateId;
                }
            }
            else {
                const baseSwap = swapMap.get(pool.baseSwap.contractAddress);
                const metaSwap = swapMap.get(pool.metaSwap.contractAddress);
                if (metaSwap && baseSwap) {
                    pool.updateSwap(baseSwap, metaSwap);
                    ++this.stateId;
                }
            }
        });
    }
    _getProspectiveTokens(t0, t1) {
        const set = new Set([
            t0,
            t1,
            ...router_config_1.BASES_TO_CHECK_TRADES_AGAINST[this.chainId],
            ...(router_config_1.ADDITIONAL_BASES[this.chainId]?.[t0.address] || []),
            ...(router_config_1.ADDITIONAL_BASES[this.chainId]?.[t1.address] || []),
        ]);
        return Array.from(set);
    }
    startFetchPoolsData() {
        this.stopFetchPoolsData();
        this.poolCodes = [];
        this.fetchedTokens.clear();
        this.getPools(router_config_1.BASES_TO_CHECK_TRADES_AGAINST[this.chainId]); // starting the process
        this.unwatchBlockNumber = this.client.watchBlockNumber({
            onBlockNumber: (blockNumber) => {
                this.lastUpdateBlock = Number(blockNumber);
                this.updatePoolsData();
            },
            onError: (error) => {
                console.error(error.message);
            },
        });
    }
    async fetchPoolsForToken(t0, t1) {
        await this.getPools(this._getProspectiveTokens(t0, t1));
    }
    getCurrentPoolList() {
        return this.poolCodes;
    }
    stopFetchPoolsData() {
        if (this.unwatchBlockNumber)
            this.unwatchBlockNumber();
    }
}
exports.SaddleBaseProvider = SaddleBaseProvider;
